import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Shield, Heart, MessageCircle } from 'lucide-react';
import { track, whatsappUrl } from '../lib/track';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_frelon33-experts/artifacts/yswmny6u_logo%20principal.png"
                alt="Frelon33™ Logo" 
                className="h-12 w-auto frelon-float"
              />
              <div>
                <h3 className="text-xl font-black frelon33-brand text-yellow-400">FRELON33™</h3>
                <p className="text-sm text-gray-400 font-medium">Expert anti-nuisibles</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Spécialiste agréé dans la destruction de nids de frelons, guêpes et autres nuisibles en Gironde. 
              Intervention rapide et sécurisée par <span className="frelon33-brand font-semibold">Frelon33™</span>.
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-yellow-400">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 group">
                <Phone className="h-4 w-4 text-yellow-400 group-hover:scale-110 transition-transform" />
                <a 
                  href="tel:0676583038" 
                  onClick={() => track('phone_click', { location: 'footer' })}
                  className="text-gray-300 hover:text-yellow-400 transition-colors font-medium"
                  data-gtm-event="phone_click"
                >
                  06 76 58 30 38
                </a>
              </div>
              <div className="flex items-center space-x-3 group">
                <MessageCircle className="h-4 w-4 text-yellow-400 group-hover:scale-110 transition-transform" />
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('whatsapp_click', { location: 'footer' })}
                  className="text-gray-300 hover:text-yellow-400 transition-colors font-medium"
                  data-gtm-event="whatsapp_click"
                >
                  WhatsApp
                </a>
              </div>
              <div className="flex items-center space-x-3 group">
                <Mail className="h-4 w-4 text-yellow-400 group-hover:scale-110 transition-transform" />
                <a 
                  href="mailto:frelon33000@gmail.com" 
                  className="text-gray-300 hover:text-yellow-400 transition-colors font-medium"
                >
                  frelon33000@gmail.com
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-yellow-400 mt-1" />
                <span className="text-gray-300 font-medium">
                  Toute la Gironde<br />
                  Bordeaux et agglomération
                </span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-yellow-400">Nos Services</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="hover:text-yellow-400 transition-colors cursor-pointer font-medium">
                Destruction nids de frelons
              </li>
              <li className="hover:text-yellow-400 transition-colors cursor-pointer font-medium">
                Élimination guêpes
              </li>
              <li className="hover:text-yellow-400 transition-colors cursor-pointer font-medium">
                Désinsectisation urgente
              </li>
              <li className="hover:text-yellow-400 transition-colors cursor-pointer font-medium">
                Intervention 24h/24
              </li>
            </ul>
          </div>

          {/* Guarantees */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-yellow-400">Nos Garanties</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-yellow-400" />
                <span className="text-gray-300 text-sm font-medium">Intervention sous 48h</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="h-4 w-4 text-yellow-400" />
                <span className="text-gray-300 text-sm font-medium">Entreprise agréée</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-yellow-400" />
                <span className="text-gray-300 text-sm font-medium">Déplacement gratuit</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer Ultra-Moderne */}
      <div className="border-t border-gray-800 bg-black/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <div className="text-gray-400 text-sm font-medium">
                © {currentYear} <span className="frelon33-brand">Frelon33™</span>. Tous droits réservés.
              </div>
              <div className="flex items-center space-x-1 text-gray-400 text-sm">
                <span>Produit d'amour par</span>
                <a 
                  href="https://vibesite.fr" 
                  target="_blank" 
                  rel="noopener"
                  className="text-yellow-400 hover:text-yellow-300 transition-colors font-bold ml-1"
                >
                  VibeSite™
                </a>
              </div>
            </div>
            <div className="flex flex-wrap space-x-4 space-y-2 text-sm">
              <Link 
                to="/mentions-legales" 
                className="text-gray-400 hover:text-yellow-400 transition-colors font-medium"
              >
                Mentions légales
              </Link>
              <Link 
                to="/cgu" 
                className="text-gray-400 hover:text-yellow-400 transition-colors font-medium"
              >
                CGU
              </Link>
              <Link 
                to="/politique-confidentialite" 
                className="text-gray-400 hover:text-yellow-400 transition-colors font-medium"
              >
                Confidentialité
              </Link>
              <Link 
                to="/cgv" 
                className="text-gray-400 hover:text-yellow-400 transition-colors font-medium"
              >
                CGV
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;